import React, { useState, useEffect } from 'react';
import { Snowflake, Heart, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StreakFreezeCard — Spend 50 Hearts to save your streak on a missed day.
 * Shows freeze inventory, cost, and activate button.
 */
const StreakFreezeCard = () => {
  const [data, setData] = useState(null);
  const [activating, setActivating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    if (!token) return;
    fetch(`${API_URL}/api/engagement/v2/streak-freeze`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);

  const handleActivate = async () => {
    setActivating(true);
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const res = await fetch(`${API_URL}/api/engagement/v2/streak-freeze/activate`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) {
        setResult({ success: true, msg: `Streak saved! -${d.cost} Hearts` });
        setData(prev => ({ ...prev, freezes_available: prev.freezes_available - 1, is_frozen_today: true, hearts_balance: d.hearts_remaining }));
      } else {
        setResult({ success: false, msg: d.detail || 'Failed' });
      }
    } catch { setResult({ success: false, msg: 'Network error' }); }
    finally { setActivating(false); }
  };

  if (!data) return null;

  return (
    <div className={`rounded-xl p-4 border ${data.is_frozen_today ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} data-testid="streak-freeze-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${data.is_frozen_today ? 'bg-blue-200' : 'bg-gray-100'}`}>
            <Snowflake size={16} className={data.is_frozen_today ? 'text-blue-600' : 'text-gray-500'} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">Streak Freeze</p>
            <p className="text-xs text-gray-500">{data.freezes_available}/{data.max_per_month} left this month</p>
          </div>
        </div>
        {data.is_frozen_today && (
          <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">FROZEN</span>
        )}
      </div>

      {result && (
        <div className={`text-xs p-2 rounded-lg mb-2 ${result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {result.msg}
        </div>
      )}

      {!data.is_frozen_today && data.freezes_available > 0 && (
        <button
          onClick={handleActivate}
          disabled={activating || !data.can_afford}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm font-semibold rounded-lg transition-all disabled:opacity-50 bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-md"
          data-testid="streak-freeze-activate-btn"
        >
          <Snowflake size={14} />
          {activating ? 'Freezing...' : `Freeze Streak (${data.cost}`}
          {!activating && <Heart size={12} fill="currentColor" />}
          {!activating && ')'}
        </button>
      )}

      {!data.can_afford && !data.is_frozen_today && (
        <p className="text-xs text-amber-600 flex items-center gap-1 mt-2">
          <AlertTriangle size={12} /> Need {data.cost} Hearts (you have {data.hearts_balance})
        </p>
      )}
    </div>
  );
};

export default StreakFreezeCard;
